import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Shield, UserPlus, RefreshCw, Plus, Edit, IdCard } from "lucide-react";
import { UserRole } from "@/pages/users/types";
import { useUserRoles } from "@/hooks/useUserRoles";
import { DeleteAdminDialog } from "./DeleteAdminDialog";

const PROTECTED_SUPER_ADMIN_EMAIL = "drivas@ovitsec.com";

interface UserWithRole {
  id: string;
  email: string;
  full_name: string;
  roles: UserRole[];
  created_at: string;
}

export function UserManagement() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useUserRoles();
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<UserRole>("guard");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserWithRole | null>(null);
  const [viewingUser, setViewingUser] = useState<UserWithRole | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: "",
    password: "",
    fullName: "",
    firstName: "",
    lastName: "",
    phone: "",
    mobilePhone: "",
    address: "",
    dateOfBirth: "",
    afm: "",
    identityNumber: "",
    emergencyContact: "",
    role: "guard" as UserRole
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Fetch all profiles with all fields
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, email, full_name, first_name, last_name, phone, mobile_phone, address, date_of_birth, emergency_contact, afm, identity_number, created_at')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Fetch all user roles
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');

      if (rolesError) throw rolesError;

      // Combine profiles with their roles
      const usersWithRoles: UserWithRole[] = profiles?.map(profile => ({
        ...profile,
        roles: userRoles?.filter(ur => ur.user_id === profile.id).map(ur => ur.role as UserRole) || []
      })) || [];

      setUsers(usersWithRoles);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddRole = async () => {
    if (!selectedUserId || !selectedRole) {
      toast({
        title: "Error",
        description: "Please select a user and role",
        variant: "destructive",
      });
      return;
    }

    const user = users.find(u => u.id === selectedUserId);
    
    // Prevent adding super_admin to anyone except the protected email
    if (selectedRole === 'super_admin' && user?.email !== PROTECTED_SUPER_ADMIN_EMAIL) {
      toast({
        title: "Access Denied",
        description: "Only the global super admin can have this role",
        variant: "destructive",
      });
      return;
    }

    try {
      // Update profiles.Role field to match the role being added
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ Role: selectedRole })
        .eq('id', selectedUserId);

      if (profileError) {
        console.error('Error updating profile role:', profileError);
        // Continue even if profile update fails
      }

      // Add role to user_roles table
      const { error } = await supabase
        .from('user_roles')
        .insert([{ user_id: selectedUserId, role: selectedRole }]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Role added successfully",
      });
      
      fetchUsers();
      setSelectedUserId("");
      setSelectedRole("guard");
    } catch (error: any) {
      console.error('Error adding role:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to add role",
        variant: "destructive",
      });
    }
  };


  const handleCreateUser = async () => {
    if (!newUserData.email || !newUserData.password || !newUserData.fullName) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    if (newUserData.password.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return;
    }

    try {
      setCreating(true);
      
      // Try Edge Function first
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: newUserData.email,
          password: newUserData.password,
          fullName: newUserData.fullName,
          firstName: newUserData.firstName,
          lastName: newUserData.lastName,
          phone: newUserData.phone,
          mobilePhone: newUserData.mobilePhone,
          address: newUserData.address,
          dateOfBirth: newUserData.dateOfBirth,
          afm: newUserData.afm,
          identityNumber: newUserData.identityNumber,
          emergencyContact: newUserData.emergencyContact,
          role: newUserData.role
        }
      });

      // If Edge Function fails, use fallback direct creation
      if (error || !data?.success) {
        console.log('Edge function failed, using fallback method');
        
        // Create auth user
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: newUserData.email,
          password: newUserData.password,
          options: {
            data: {
              full_name: newUserData.fullName
            }
          }
        });

        if (authError) throw authError;
        if (!authData.user) throw new Error('Failed to create user');

        // Update profile with additional fields
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            full_name: newUserData.fullName,
            first_name: newUserData.firstName,
            last_name: newUserData.lastName,
            phone: newUserData.phone,
            mobile_phone: newUserData.mobilePhone,
            address: newUserData.address,
            date_of_birth: newUserData.dateOfBirth,
            afm: newUserData.afm,
            identity_number: newUserData.identityNumber,
            emergency_contact: newUserData.emergencyContact
          })
          .eq('id', authData.user.id);

        if (profileError) throw profileError;

        // Assign role
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: authData.user.id,
            role: newUserData.role
          });

        if (roleError) throw roleError;
      }

      toast({
        title: "Success",
        description: "User created successfully",
      });

      setCreateDialogOpen(false);
      fetchUsers(); // Refresh the user list
      setNewUserData({
        email: "",
        password: "",
        fullName: "",
        firstName: "",
        lastName: "",
        phone: "",
        mobilePhone: "",
        address: "",
        dateOfBirth: "",
        afm: "",
        identityNumber: "",
        emergencyContact: "",
        role: "guard"
      });
      fetchUsers();
    } catch (error: any) {
      console.error('Error creating user:', error);
      
      // Extract meaningful error message
      let errorMessage = "Failed to create user";
      if (error.message) {
        // Check for common error patterns
        if (error.message.includes("already been registered")) {
          errorMessage = "A user with this email already exists";
        } else if (error.message.includes("email_exists")) {
          errorMessage = "A user with this email already exists";
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'default';
      case 'admin':
        return 'secondary';
      case 'manager':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading users...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                User Management
              </CardTitle>
              <CardDescription>
                Manage user roles and permissions. Only super admins can access this.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="default" size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Create User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                    <DialogDescription>
                      Create a new user account and assign their role. The user will be created in Supabase.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto pr-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-email">Email *</Label>
                        <Input
                          id="new-email"
                          type="email"
                          placeholder="user@example.com"
                          value={newUserData.email}
                          onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-password">Password *</Label>
                        <Input
                          id="new-password"
                          type="password"
                          placeholder="Minimum 6 characters"
                          value={newUserData.password}
                          onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-fullname">Full Name *</Label>
                        <Input
                          id="new-fullname"
                          type="text"
                          placeholder="John Doe"
                          value={newUserData.fullName}
                          onChange={(e) => setNewUserData({ ...newUserData, fullName: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-role">Role *</Label>
                        <Select 
                          value={newUserData.role} 
                          onValueChange={(value) => setNewUserData({ ...newUserData, role: value as UserRole })}
                        >
                          <SelectTrigger id="new-role">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="guard">Guard</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="client">Client</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-firstname">First Name</Label>
                        <Input
                          id="new-firstname"
                          type="text"
                          placeholder="John"
                          value={newUserData.firstName}
                          onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-lastname">Last Name</Label>
                        <Input
                          id="new-lastname"
                          type="text"
                          placeholder="Doe"
                          value={newUserData.lastName}
                          onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-phone">Phone</Label>
                        <Input
                          id="new-phone"
                          type="tel"
                          placeholder="+30 210 1234567"
                          value={newUserData.phone}
                          onChange={(e) => setNewUserData({ ...newUserData, phone: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-mobile">Mobile Phone</Label>
                        <Input
                          id="new-mobile"
                          type="tel"
                          placeholder="+30 690 1234567"
                          value={newUserData.mobilePhone}
                          onChange={(e) => setNewUserData({ ...newUserData, mobilePhone: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="new-address">Address</Label>
                      <Input
                        id="new-address"
                        type="text"
                        placeholder="123 Main St, Athens"
                        value={newUserData.address}
                        onChange={(e) => setNewUserData({ ...newUserData, address: e.target.value })}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-dob">Date of Birth</Label>
                        <Input
                          id="new-dob"
                          type="date"
                          value={newUserData.dateOfBirth}
                          onChange={(e) => setNewUserData({ ...newUserData, dateOfBirth: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-emergency">Emergency Contact</Label>
                        <Input
                          id="new-emergency"
                          type="tel"
                          placeholder="+30 690 7654321"
                          value={newUserData.emergencyContact}
                          onChange={(e) => setNewUserData({ ...newUserData, emergencyContact: e.target.value })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-afm">AFM (Tax ID)</Label>
                        <Input
                          id="new-afm"
                          type="text"
                          placeholder="123456789"
                          value={newUserData.afm}
                          onChange={(e) => setNewUserData({ ...newUserData, afm: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="new-identity">Identity Number</Label>
                        <Input
                          id="new-identity"
                          type="text"
                          placeholder="AB123456"
                          value={newUserData.identityNumber}
                          onChange={(e) => setNewUserData({ ...newUserData, identityNumber: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateDialogOpen(false)} disabled={creating}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateUser} disabled={creating}>
                      {creating ? "Creating..." : "Create User"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Button onClick={fetchUsers} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Add Role Section */}
          <div className="border rounded-lg p-4 space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              Assign Role to User
            </h3>
            <div className="bg-muted/50 p-3 rounded-md text-sm space-y-2">
              <p className="font-medium">Role Hierarchy & Permissions:</p>
              <ul className="space-y-1 ml-4">
                <li><strong>Super Admin:</strong> Global access to all resources and users (Protected - only drivas@ovitsec)</li>
                <li><strong>Admin:</strong> Full admin capabilities but scoped to resources they create</li>
                <li><strong>Manager:</strong> Can manage assigned teams and their members</li>
                <li><strong>Client:</strong> Can view and evaluate assigned teams</li>
                <li><strong>Guard:</strong> Standard user access</li>
              </ul>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Select User</Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a user" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.full_name || user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Select Role</Label>
                <Select value={selectedRole} onValueChange={(value) => setSelectedRole(value as UserRole)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="guard">Guard</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="super_admin" disabled={users.find(u => u.id === selectedUserId)?.email !== PROTECTED_SUPER_ADMIN_EMAIL}>
                      Super Admin (Protected)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-end">
                <Button onClick={handleAddRole} className="w-full">
                  Add Role
                </Button>
              </div>
            </div>
          </div>

          {/* Users List */}
          <div className="space-y-3">
            <h3 className="font-semibold">Admins & Super Admins ({users.filter(u => u.roles.some(r => r === 'admin' || r === 'super_admin')).length})</h3>
            <div className="space-y-2">
              {users.filter(user => user.roles.some(role => role === 'admin' || role === 'super_admin')).map(user => (
                <Card 
                  key={user.id} 
                  className={`${user.email === PROTECTED_SUPER_ADMIN_EMAIL ? 'border-primary' : ''} ${user.email !== PROTECTED_SUPER_ADMIN_EMAIL ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
                  onClick={() => {
                    if (user.email !== PROTECTED_SUPER_ADMIN_EMAIL) {
                      navigate(`/admin/${user.id}`);
                    }
                  }}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{user.full_name || 'No name'}</p>
                          {user.email === PROTECTED_SUPER_ADMIN_EMAIL && (
                            <Badge variant="default" className="gap-1">
                              <Shield className="h-3 w-3" />
                              Global Super Admin
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                        <div className="flex gap-2 flex-wrap mt-2">
                          {user.roles.length > 0 ? (
                            user.roles.map(role => (
                              <Badge key={role} variant={getRoleBadgeVariant(role)}>
                                {role}
                              </Badge>
                            ))
                          ) : (
                            <Badge variant="outline">No roles assigned</Badge>
                          )}
                        </div>
                      </div>
                      {/* View, Edit and Delete Buttons */}
                      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={async (e) => {
                            e.stopPropagation();
                            console.log('Fetching user details for:', user.id);
                            // Fetch full user data
                            const { data: fullUser, error } = await supabase
                              .from('profiles')
                              .select('*')
                              .eq('id', user.id)
                              .single();
                            
                            console.log('Fetched user data:', fullUser);
                            console.log('Error:', error);
                            
                            if (fullUser) {
                              const mergedUser = { ...user, ...fullUser };
                              console.log('Merged user data:', mergedUser);
                              setViewingUser(mergedUser);
                              console.log('Opening view dialog...');
                              setViewDialogOpen(true);
                              console.log('Dialog state set to true');
                            } else {
                              toast({
                                title: "Error",
                                description: "Failed to load user details",
                                variant: "destructive",
                              });
                            }
                          }}
                          title="View Details"
                        >
                          <IdCard className="h-4 w-4" />
                        </Button>
                        {isSuperAdmin && user.email !== PROTECTED_SUPER_ADMIN_EMAIL && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingUser(user);
                                setEditDialogOpen(true);
                              }}
                              title="Edit Details"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <DeleteAdminDialog
                              userId={user.id}
                              userName={user.full_name || user.email || 'Unknown'}
                              userEmail={user.email || ''}
                              onDeleteComplete={fetchUsers}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Admin Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Admin Details</DialogTitle>
            <DialogDescription>
              Update contact information for {editingUser?.full_name || editingUser?.email}
            </DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-full-name">Full Name</Label>
                  <Input
                    id="edit-full-name"
                    value={editingUser.full_name}
                    onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-first-name">First Name</Label>
                  <Input
                    id="edit-first-name"
                    value={(editingUser as any).first_name || ''}
                    onChange={(e) => setEditingUser({...editingUser, first_name: e.target.value} as any)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-last-name">Last Name</Label>
                  <Input
                    id="edit-last-name"
                    value={(editingUser as any).last_name || ''}
                    onChange={(e) => setEditingUser({...editingUser, last_name: e.target.value} as any)}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Phone</Label>
                  <Input
                    id="edit-phone"
                    value={(editingUser as any).phone || ''}
                    onChange={(e) => setEditingUser({...editingUser, phone: e.target.value} as any)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-mobile-phone">Mobile Phone</Label>
                  <Input
                    id="edit-mobile-phone"
                    value={(editingUser as any).mobile_phone || ''}
                    onChange={(e) => setEditingUser({...editingUser, mobile_phone: e.target.value} as any)}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-address">Address</Label>
                <Input
                  id="edit-address"
                  value={(editingUser as any).address || ''}
                  onChange={(e) => setEditingUser({...editingUser, address: e.target.value} as any)}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-dob">Date of Birth</Label>
                  <Input
                    id="edit-dob"
                    type="date"
                    value={(editingUser as any).date_of_birth || ''}
                    onChange={(e) => setEditingUser({...editingUser, date_of_birth: e.target.value} as any)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-emergency">Emergency Contact</Label>
                  <Input
                    id="edit-emergency"
                    value={(editingUser as any).emergency_contact || ''}
                    onChange={(e) => setEditingUser({...editingUser, emergency_contact: e.target.value} as any)}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-afm">AFM (Tax ID)</Label>
                  <Input
                    id="edit-afm"
                    value={(editingUser as any).afm || ''}
                    onChange={(e) => setEditingUser({...editingUser, afm: e.target.value} as any)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-identity">Identity Number</Label>
                  <Input
                    id="edit-identity"
                    value={(editingUser as any).identity_number || ''}
                    onChange={(e) => setEditingUser({...editingUser, identity_number: e.target.value} as any)}
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={async () => {
              if (!editingUser) return;
              setUpdating(true);
              try {
                const updateData: any = {
                  full_name: editingUser.full_name,
                  email: editingUser.email,
                  first_name: (editingUser as any).first_name,
                  last_name: (editingUser as any).last_name,
                  phone: (editingUser as any).phone,
                  mobile_phone: (editingUser as any).mobile_phone,
                  address: (editingUser as any).address,
                  date_of_birth: (editingUser as any).date_of_birth,
                  emergency_contact: (editingUser as any).emergency_contact,
                  afm: (editingUser as any).afm,
                  identity_number: (editingUser as any).identity_number
                };

                const { error } = await supabase
                  .from('profiles')
                  .update(updateData)
                  .eq('id', editingUser.id);

                if (error) throw error;

                toast({
                  title: "Success",
                  description: "Admin details updated successfully",
                });
                setEditDialogOpen(false);
                fetchUsers();
              } catch (error: any) {
                toast({
                  title: "Error",
                  description: error.message,
                  variant: "destructive",
                });
              } finally {
                setUpdating(false);
              }
            }} disabled={updating}>
              {updating ? "Updating..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Admin Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Admin Contact Details</DialogTitle>
            <DialogDescription>
              View contact information for {viewingUser?.full_name || viewingUser?.email}
            </DialogDescription>
          </DialogHeader>
          {viewingUser && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto p-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  <p className="text-sm font-medium">{viewingUser.email || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Full Name</Label>
                  <p className="text-sm font-medium">{viewingUser.full_name || '-'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">First Name</Label>
                  <p className="text-sm font-medium">{(viewingUser as any).first_name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Last Name</Label>
                  <p className="text-sm font-medium">{(viewingUser as any).last_name || '-'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <p className="text-sm font-medium">{(viewingUser as any).phone || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Mobile Phone</Label>
                  <p className="text-sm font-medium">{(viewingUser as any).mobile_phone || '-'}</p>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Address</Label>
                <p className="text-sm font-medium">{(viewingUser as any).address || '-'}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Date of Birth</Label>
                  <p className="text-sm font-medium">{(viewingUser as any).date_of_birth || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Emergency Contact</Label>
                  <p className="text-sm font-medium">{(viewingUser as any).emergency_contact || '-'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">AFM (Tax ID)</Label>
                  <p className="text-sm font-medium">{(viewingUser as any).afm || '-'}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Identity Number</Label>
                  <p className="text-sm font-medium">{(viewingUser as any).identity_number || '-'}</p>
                </div>
              </div>
              
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Roles</Label>
                <div className="flex gap-2 flex-wrap">
                  {viewingUser.roles?.map(role => (
                    <Badge key={role} variant={getRoleBadgeVariant(role)}>
                      {role}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}